import React, { useEffect, useState } from "react";
import { View, Text, StyleSheet, Dimensions } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChildStackParamList } from "../../navigation/types";
import { pairingApi } from "../../api/pairing";
import { useAuthStore } from "../../state/authStore";
import { Mascot } from "../../components/Mascot";
import { QrCode } from "../../components/QrCode";
import { pairUrl } from "../../web-url";
import { colors, fontFamily } from "../../theme";

type Props = NativeStackScreenProps<ChildStackParamList, "Pairing">;

const { width, height } = Dimensions.get("window");

const scale = (size: number) => (width / 1920) * size;

export default function PairingScreen({ navigation }: Props) {
  const [code, setCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(300);
  const { setTokens } = useAuthStore();

  const fetchCode = async () => {
    const res = await pairingApi.start();
    setCode(res.code);
    setSecondsLeft(300);
  };

  useEffect(() => {
    fetchCode();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      fetchCode();
      return;
    }
    const t = setInterval(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  useEffect(() => {
    if (!code) return;
    const interval = setInterval(async () => {
      try {
        const res = await pairingApi.exchange(code);
        // Persist parentId AND tvId. tvId is what lets the gateway bucket
        // this TV's socket per-device, so the parent can target Stop /
        // Disconnect at this specific TV instead of broadcasting to all.
        // Older backends omit res.tvId — setTokens leaves prev.tvId alone
        // in that case, which is the right backward-compat behavior.
        setTokens(res.accessToken, "", res.parentId, res.tvId);
        clearInterval(interval);
        navigation.replace("ProfileSelect");
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [code]);

  const minutes = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const digits = code ? code.split("") : ["—", "—", "—", "—", "—", "—"];

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <View style={styles.mascotSection}>
          <Mascot mood="wave" color={colors.primary} size={scale(360)} bob />
        </View>

        <View style={styles.contentSection}>
          <Text style={styles.eyebrow}>PAIR THIS TV WITH A PARENT</Text>
          <Text style={styles.title}>
            Open Kidly on your phone and enter this code
          </Text>

          <View style={styles.codeRow}>
            {digits.map((d, i) => (
              <View key={i} style={styles.digitBox}>
                <Text style={styles.digit}>{d}</Text>
              </View>
            ))}
          </View>

          <View style={styles.timerRow}>
            <View style={styles.timerDot} />
            <Text style={styles.timerText}>
              Code refreshes in {minutes}:{secs.toString().padStart(2, "0")}
            </Text>
          </View>
        </View>

        {/* Scan-to-pair: phone scans the QR, web opens with the code
            prefilled in PairTvScreen. Faster than typing 6 digits. */}
        {code && (
          <View style={styles.qrCol}>
            <Text style={styles.qrEyebrow}>OR SCAN</Text>
            <View style={styles.qrFrame}>
              <QrCode value={pairUrl(code)} size={scale(200)} />
            </View>
            <Text style={styles.qrCaption}>
              Scan with a parent's phone to skip typing.
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Full-screen wrapper centers the row block on both axes so the design
  // doesn't cluster in the left third on a wide TV viewport.
  // Horizontal padding stays generous (~60px on a 1920 TV) so the QR + caption
  // never touch the overscan zone on consumer Samsung/LG sets (~3-5%).
  //
  // Why explicit `width` + `height` instead of `flex: 1`: Tizen 5.5 / webOS 5
  // run Chromium 76 in fullscreen-widget mode. Their flex propagation from
  // html→body→#root→RN-Web-wrappers→App's outer flex View → PairingScreen
  // is unreliable — observed in the wild as content sticking to the top
  // of the screen with an empty bottom half (justifyContent: "center" has
  // nothing tall to center against). Locking to Dimensions.get('window')
  // sidesteps the whole flex chain.
  container: {
    width,
    height,
    backgroundColor: colors.bg,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: scale(60),
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  mascotSection: {
    width: scale(380),
    alignItems: "center",
    justifyContent: "center",
    marginRight: scale(48), // gap replacement (Chromium <84)
  },
  contentSection: {
    // content width — fits the headline + 6 digit boxes comfortably
    width: scale(860),
    marginRight: scale(48), // gap to QR column
  },
  eyebrow: {
    fontFamily,
    fontSize: scale(26),
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 3,
    marginBottom: scale(18),
  },
  title: {
    fontFamily,
    fontSize: scale(84),
    fontWeight: "900",
    color: colors.ink,
    lineHeight: scale(92),
    letterSpacing: -1.4,
    marginBottom: scale(48),
  },
  codeRow: {
    flexDirection: "row",
    marginBottom: scale(36),
  },
  digitBox: {
    width: scale(130),
    height: scale(160),
    backgroundColor: colors.surface,
    borderRadius: scale(24),
    borderWidth: 4,
    borderColor: colors.ink,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: colors.ink,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 6,
    marginRight: scale(16), // gap replacement
  },
  digit: {
    fontFamily,
    fontSize: scale(96),
    fontWeight: "900",
    color: colors.ink,
    lineHeight: scale(108),
  },
  timerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  timerDot: {
    width: scale(14),
    height: scale(14),
    borderRadius: scale(7),
    backgroundColor: colors.primary,
    marginRight: scale(12), // gap replacement
  },
  timerText: {
    fontFamily,
    fontSize: scale(26),
    fontWeight: "700",
    color: colors.ink2,
  },
  qrCol: {
    alignItems: "center",
  },
  qrEyebrow: {
    fontFamily,
    fontSize: scale(18),
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 2,
    marginBottom: scale(12),
  },
  qrFrame: {
    padding: scale(14),
    backgroundColor: colors.surface,
    borderRadius: scale(18),
    borderWidth: 3,
    borderColor: colors.ink,
    marginBottom: scale(12),
  },
  qrCaption: {
    fontFamily,
    fontSize: scale(16),
    fontWeight: "600",
    color: colors.ink3,
    textAlign: "center",
    maxWidth: scale(220),
  },
});
